// Doc data → typed rows. Pure: takes plain objects (Firestore Timestamps duck-typed via toMillis).
import { CONFIG, CONV, LEDGER, MSG, RECIPIENT, RUN } from "@/lib/fields";
import type { Conversation, LedgerRow, Ms, Recipient, Run, ThreadMessage, WaConfig, WaError } from "./types";

type Data = Record<string, unknown>;

export function toMs(v: unknown): Ms {
  if (v == null) return null;
  if (typeof v === "number") return v;
  if (v instanceof Date) return v.getTime();
  if (typeof v === "string") {
    const t = Date.parse(v);
    return Number.isNaN(t) ? null : t;
  }
  if (typeof v === "object" && typeof (v as { toMillis?: unknown }).toMillis === "function") {
    return (v as { toMillis: () => number }).toMillis();
  }
  return null;
}

const str = (v: unknown): string => (v == null ? "" : String(v));
const strOrNull = (v: unknown): string | null => (v == null || v === "" ? null : String(v));
const num = (v: unknown, d = 0): number => (typeof v === "number" && Number.isFinite(v) ? v : d);

function toError(v: unknown): WaError | null {
  if (!v || typeof v !== "object") return null;
  const e = v as Data;
  const code = e.code == null ? null : typeof e.code === "number" ? e.code : String(e.code);
  return { code, title: str(e.title), detail: str(e.detail) };
}

function toStringMap(v: unknown): Record<string, string> {
  if (!v || typeof v !== "object") return {};
  return Object.fromEntries(Object.entries(v as Data).map(([k, x]) => [k, str(x)]));
}

export function toLedgerRow(id: string, d: Data): LedgerRow {
  return {
    key: id,
    status: str(d[LEDGER.status]),
    phone: strOrNull(d[LEDGER.phone]),
    name: str(d[LEDGER.name]),
    wamid: strOrNull(d[LEDGER.wamid]),
    template: strOrNull(d[LEDGER.template]),
    vars: toStringMap(d[LEDGER.vars]),
    sentAt: toMs(d[LEDGER.sentAt]),
    deliveredAt: toMs(d[LEDGER.deliveredAt]),
    readAt: toMs(d[LEDGER.readAt]),
    failedAt: toMs(d[LEDGER.failedAt]),
    lastAttemptAt: toMs(d[LEDGER.lastAttemptAt]),
    windowAt: toMs(d[LEDGER.windowAt]),
    retryApprovedAt: toMs(d[LEDGER.retryApprovedAt]),
    attempts: num(d[LEDGER.attempts]),
    error: toError(d[LEDGER.error]),
  };
}

export function toRecipient(id: string, d: Data): Recipient {
  const phone = strOrNull(d[RECIPIENT.phone]);
  const sessions = d[RECIPIENT.sessions];
  return {
    id,
    key: str(d[RECIPIENT.key]) || id,
    baseKey: str(d[RECIPIENT.baseKey]) || id,
    phone,
    rawPhone: str(d[RECIPIENT.rawPhone]),
    phoneOk: d[RECIPIENT.phoneOk] == null ? phone != null : Boolean(d[RECIPIENT.phoneOk]),
    phoneError: strOrNull(d[RECIPIENT.phoneError]),
    name: str(d[RECIPIENT.name]),
    days: str(d[RECIPIENT.days]),
    dates: str(d[RECIPIENT.dates]),
    sessions: Array.isArray(sessions) ? sessions.map(str) : [],
    sheetTs: strOrNull(d[RECIPIENT.sheetTs]),
    params: d[RECIPIENT.params] && typeof d[RECIPIENT.params] === "object" ? toStringMap(d[RECIPIENT.params]) : null,
    publishedAt: toMs(d[RECIPIENT.publishedAt]),
  };
}

export function toConversation(id: string, d: Data): Conversation {
  const regIds = d[CONV.regIds];
  const events = d[CONV.events];
  return {
    phone: id,
    name: str(d[CONV.name]),
    profileName: str(d[CONV.profileName]),
    lastAt: toMs(d[CONV.lastAt]),
    lastInboundAt: toMs(d[CONV.lastInboundAt]),
    lastPreview: str(d[CONV.lastPreview]),
    unread: num(d[CONV.unread]),
    optedOut: Boolean(d[CONV.optedOut]),
    regIds: Array.isArray(regIds) ? regIds.map(str) : [],
    events: Array.isArray(events) ? events.map(str) : [],
  };
}

export function toThreadMessage(id: string, d: Data): ThreadMessage {
  return {
    id,
    dir: d[MSG.dir] === "in" ? "in" : "out",
    kind: strOrNull(d[MSG.kind]),
    text: str(d[MSG.text]),
    template: strOrNull(d[MSG.template]),
    regId: strOrNull(d[MSG.regId]),
    ledgerPath: strOrNull(d[MSG.ledgerPath]),
    status: strOrNull(d[MSG.status]),
    at: toMs(d[MSG.at]),
    sentBy: strOrNull(d[MSG.sentBy]),
    error: toError(d[MSG.error]),
    ourReaction: strOrNull((d[MSG.ourReaction] as Data | null | undefined)?.emoji),
  };
}

export function toRun(id: string, d: Data): Run {
  const ap = d[RUN.attemptedPhones];
  return {
    id,
    startedAt: toMs(d[RUN.startedAt]),
    finishedAt: toMs(d[RUN.finishedAt]),
    by: str(d[RUN.by]),
    limit: typeof d[RUN.limit] === "number" ? (d[RUN.limit] as number) : null,
    template: str(d[RUN.template]),
    attemptedPhones: Array.isArray(ap) ? ap.length : num(ap),
    sent: num(d[RUN.sent]),
    failed: num(d[RUN.failed]),
    invalid: num(d[RUN.invalid]),
    stopReason: strOrNull(d[RUN.stopReason]),
  };
}

export function toConfig(d: Data | undefined): WaConfig | null {
  if (!d) return null;
  const src = (d[CONFIG.source] ?? {}) as Data;
  return {
    eventName: str(d[CONFIG.eventName]),
    template: str(d[CONFIG.template]),
    templateBody: str(d[CONFIG.templateBody]),
    params: toStringMap(d[CONFIG.params]),
    dailyCap: num(d[CONFIG.dailyCap]),
    source: { kind: str(src.kind), name: str(src.name), ...(src.sheet_id ? { sheetId: str(src.sheet_id) } : {}) },
    publishedAt: toMs(d[CONFIG.publishedAt]),
  };
}
